import { useEffect, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Archive, ArrowRight, CheckCircle2, Clock, FileText, Mail, Pause, Play, RefreshCw, Rocket, Send, Smartphone, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CampaignEnrollmentDialog from "./CampaignEnrollmentDialog";
import { SequenceBuilderDialog } from "./SequenceBuilderDialog";
import { CampaignDeliveryLogsDialog } from "./campaigns/CampaignDeliveryLogsDialog";
import { CampaignTestSendDialog } from "./campaigns/CampaignTestSendDialog";

type Campaign = {
  id: string;
  name: string;
  description: string | null;
  status: string;
  campaign_type: string;
  total_leads: number | null;
  active_leads: number | null;
  created_at: string;
  approved_at?: string | null;
  campaign_variants?: Array<{ sequence_id: string | null; email_sequences?: any | null }>;
};

type LeadRecord = {
  id: string;
  contact_name: string | null;
  contact_email?: string | null;
  email?: string | null;
  contact_phone?: string | null;
  property_address?: string | null;
  status?: string | null;
  sms_consent?: boolean | null;
  sms_opted_out_at?: string | null;
};

type CampaignMetrics = {
  enrolled: number;
  active: number;
  emailsSent: number;
  smsSent: number;
  failed: number;
  opens: number;
  clicks: number;
  replies: number;
};

type Readiness = {
  gmailConnected: boolean;
  smsEnabled: boolean;
  smsFromNumber: string | null;
  queuedEmails: number;
  failedEmails: number;
  failedSms: number;
  leadsNeedingAction: number;
};

interface CampaignManagerProps {
  selectedLeadIds?: Set<string>;
  leads?: LeadRecord[];
  onCreateCampaign?: () => void;
  onOpenSequences?: () => void;
  onRefreshParent?: () => void;
  compact?: boolean;
}

const statuses = ["all", "draft", "approved", "active", "paused", "completed"];

const emptyMetrics: CampaignMetrics = { enrolled: 0, active: 0, emailsSent: 0, smsSent: 0, failed: 0, opens: 0, clicks: 0, replies: 0 };

export function CampaignManager({ selectedLeadIds = new Set(), leads = [], onCreateCampaign, onOpenSequences, onRefreshParent, compact = false }: CampaignManagerProps) {
  const { orgId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [metrics, setMetrics] = useState<Record<string, CampaignMetrics>>({});
  const [readiness, setReadiness] = useState<Readiness>({ gmailConnected: false, smsEnabled: false, smsFromNumber: null, queuedEmails: 0, failedEmails: 0, failedSms: 0, leadsNeedingAction: 0 });
  const [activeStatus, setActiveStatus] = useState("all");
  const [enrollCampaign, setEnrollCampaign] = useState<Campaign | null>(null);
  const [testCampaign, setTestCampaign] = useState<Campaign | null>(null);
  const [logsCampaign, setLogsCampaign] = useState<Campaign | null>(null);
  const [editingSequence, setEditingSequence] = useState<any | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);

  const selectedLeads = useMemo(() => leads.filter((lead) => selectedLeadIds.has(lead.id)), [leads, selectedLeadIds]);
  const visibleCampaigns = useMemo(() => activeStatus === "all" ? campaigns : campaigns.filter((campaign) => campaign.status === activeStatus), [campaigns, activeStatus]);
  const displayedCampaigns = compact ? visibleCampaigns.slice(0, 3) : visibleCampaigns;

  useEffect(() => {
    void loadCampaignOps();
  }, [orgId]);

  const loadCampaignOps = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [campaignRes, gmailRes, orgRes, enrollRes, emailRes, smsRes, actionLeadRes] = await Promise.all([
        supabase
          .from("ai_campaigns")
          .select("*, campaign_variants(sequence_id, email_sequences(*))")
          .eq("org_id", orgId)
          .order("created_at", { ascending: false }),
        supabase.from("gmail_tokens").select("id", { count: "exact", head: true }).eq("org_id", orgId),
        supabase.from("organizations").select("sms_enabled, sms_from_number").eq("id", orgId).maybeSingle(),
        supabase.from("campaign_enrollments").select("campaign_id, status").eq("org_id", orgId),
        supabase.from("campaign_email_sends").select("enrollment_id, status, open_count, click_count, replied_at, campaign_enrollments(campaign_id)").eq("org_id", orgId),
        (supabase as any).from("campaign_sms_sends").select("enrollment_id, status, campaign_enrollments(campaign_id)").eq("org_id", orgId),
        supabase.from("leads").select("id", { count: "exact", head: true }).eq("org_id", orgId).in("status", ["new", "contacted", "qualified"]),
      ]);

      if (campaignRes.error) throw campaignRes.error;
      const nextCampaigns = (campaignRes.data || []) as Campaign[];
      const nextMetrics: Record<string, CampaignMetrics> = {};
      nextCampaigns.forEach((campaign) => { nextMetrics[campaign.id] = { ...emptyMetrics }; });

      (enrollRes.data || []).forEach((row: any) => {
        const metric = nextMetrics[row.campaign_id];
        if (!metric) return;
        metric.enrolled += 1;
        if (row.status === "active") metric.active += 1;
      });

      let queuedEmails = 0;
      let failedEmails = 0;
      (emailRes.data || []).forEach((row: any) => {
        const campaignId = row.campaign_enrollments?.campaign_id;
        const metric = campaignId ? nextMetrics[campaignId] : null;
        if (row.status === "sent") metric && (metric.emailsSent += 1);
        if (["failed", "bounced"].includes(row.status)) {
          failedEmails += 1;
          metric && (metric.failed += 1);
        }
        if (["pending", "sending"].includes(row.status)) queuedEmails += 1;
        if (metric) {
          metric.opens += row.open_count || 0;
          metric.clicks += row.click_count || 0;
          if (row.replied_at) metric.replies += 1;
        }
      });

      let failedSms = 0;
      (smsRes.data || []).forEach((row: any) => {
        const campaignId = row.campaign_enrollments?.campaign_id;
        const metric = campaignId ? nextMetrics[campaignId] : null;
        if (row.status === "sent") metric && (metric.smsSent += 1);
        if (row.status === "failed") {
          failedSms += 1;
          metric && (metric.failed += 1);
        }
      });

      setCampaigns(nextCampaigns);
      setMetrics(nextMetrics);
      setReadiness({
        gmailConnected: (gmailRes.count || 0) > 0,
        smsEnabled: Boolean((orgRes.data as any)?.sms_enabled && (orgRes.data as any)?.sms_from_number),
        smsFromNumber: (orgRes.data as any)?.sms_from_number || null,
        queuedEmails,
        failedEmails,
        failedSms,
        leadsNeedingAction: actionLeadRes.count || 0,
      });
    } catch (error: any) {
      console.error("Campaign ops load failed:", error);
      toast.error(error.message || "Failed to load campaign operations");
    } finally {
      setLoading(false);
    }
  };

  const updateCampaignStatus = async (campaign: Campaign, status: string) => {
    const patch: Record<string, any> = { status };
    if (status === "approved") patch.approved_at = new Date().toISOString();
    if (status === "active") patch.activated_at = new Date().toISOString();
    const { error } = await supabase.from("ai_campaigns").update(patch).eq("id", campaign.id);
    if (error) {
      toast.error(error.message || "Campaign update failed");
      return;
    }
    toast.success(`Campaign ${status}`);
    await loadCampaignOps();
    onRefreshParent?.();
  };

  const archiveCampaign = async (campaign: Campaign) => updateCampaignStatus(campaign, "completed");

  const openSequenceEditor = (campaign: Campaign) => {
    const sequence = campaign.campaign_variants?.find((variant) => variant.sequence_id)?.email_sequences;
    if (!sequence) {
      toast.error("This campaign has no editable sequence yet");
      return;
    }
    setEditingSequence(sequence);
    setBuilderOpen(true);
  };

  const getCampaignBlockers = (campaign: Campaign, metric: CampaignMetrics, hasSequence: boolean) => {
    const blockers: string[] = [];
    if (campaign.status === "draft") blockers.push("Approval needed");
    if (!hasSequence) blockers.push("Missing sequence");
    if (!readiness.gmailConnected) blockers.push("Gmail disconnected");
    if (metric.failed > 0) blockers.push(`${metric.failed} failed send${metric.failed === 1 ? "" : "s"}`);
    if (leads.length === 0 && metric.enrolled === 0) blockers.push("No leads loaded");
    return blockers;
  };

  const statCards = [
    { label: "Gmail", value: readiness.gmailConnected ? "Connected" : "Disconnected", icon: Mail, ok: readiness.gmailConnected },
    { label: "SMS", value: readiness.smsEnabled ? readiness.smsFromNumber || "Enabled" : "Setup needed", icon: Smartphone, ok: readiness.smsEnabled },
    { label: "Queued", value: readiness.queuedEmails, icon: Clock, ok: readiness.queuedEmails < 25 },
    { label: "Failures", value: readiness.failedEmails + readiness.failedSms, icon: AlertTriangle, ok: readiness.failedEmails + readiness.failedSms === 0 },
    { label: "Need action", value: readiness.leadsNeedingAction, icon: Users, ok: readiness.leadsNeedingAction === 0 },
  ];

  if (loading) {
    return <Card><CardContent className="flex items-center justify-center py-10 text-sm text-muted-foreground"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Loading campaign operations...</CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-5">
        {statCards.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.label} className={cn("border-border/50 bg-card/70", !item.ok && "border-destructive/30 bg-destructive/5")}>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-xs"><Icon className="h-4 w-4" />{item.label}</CardDescription>
                <CardTitle className="truncate text-lg">{item.value}</CardTitle>
              </CardHeader>
            </Card>
          );
        })}
      </div>

      <Card className="border-border/50 bg-card/70 backdrop-blur-xl">
        <CardHeader className="border-b border-border/50">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Rocket className="h-5 w-5 text-primary" />{compact ? "Campaign Snapshot" : "Campaign Command"}</CardTitle>
              <CardDescription>{compact ? "Top launch items only. Full campaign management lives on its own page." : "Approve, edit, enroll, pause, and monitor outreach from one place."}</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={onOpenSequences}><Mail className="mr-2 h-4 w-4" />Sequences</Button>
              {compact && <Button variant="outline" onClick={() => navigate("/campaigns")}>View All Campaigns<ArrowRight className="ml-2 h-4 w-4" /></Button>}
              <Button onClick={onCreateCampaign}><Rocket className="mr-2 h-4 w-4" />New Campaign</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <Tabs value={activeStatus} onValueChange={setActiveStatus}>
            <TabsList className="mb-4 flex flex-wrap justify-start bg-background/70">
              {statuses.map((status) => (
                <TabsTrigger key={status} value={status} className="capitalize">{status}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value={activeStatus} className="mt-0">
              <div className="grid gap-3">
                {displayedCampaigns.map((campaign) => {
                  const metric = metrics[campaign.id] || emptyMetrics;
                  const hasSequence = Boolean(campaign.campaign_variants?.some((variant) => variant.sequence_id));
                  const blockers = getCampaignBlockers(campaign, metric, hasSequence);
                  const ready = blockers.length === 0;
                  return (
                    <div key={campaign.id} className="rounded-lg border border-border/70 bg-background/70 p-4 shadow-sm transition-colors hover:bg-background">
                      <div className="grid gap-4 xl:grid-cols-[1.1fr_0.8fr_1fr] xl:items-start">
                        <div className="min-w-0 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className={cn("capitalize", campaign.status === "active" && "border-primary/40 bg-primary/10 text-primary", campaign.status === "approved" && "border-accent/40 bg-accent/10 text-accent-foreground", campaign.status === "paused" && "border-destructive/30 bg-destructive/10 text-destructive")}>{campaign.status}</Badge>
                            <Badge variant={ready ? "outline" : "secondary"} className={cn("gap-1", ready && "border-primary/30 bg-primary/10 text-primary")}>
                              {ready ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                              {ready ? "Ready to launch" : "Needs attention"}
                            </Badge>
                          </div>
                          <div>
                            <h3 className="truncate text-base font-semibold text-foreground">{campaign.name}</h3>
                            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{campaign.description || "No description"}</p>
                          </div>
                          <p className="text-xs text-muted-foreground">Created {formatDistanceToNow(new Date(campaign.created_at), { addSuffix: true })}</p>
                        </div>

                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6 xl:grid-cols-3">
                          {[{ label: "Enrolled", value: metric.enrolled }, { label: "Active", value: metric.active }, { label: "Emails", value: metric.emailsSent }, { label: "Texts", value: metric.smsSent }, { label: "Opens", value: metric.opens }, { label: "Failed", value: metric.failed }].map((item) => (
                            <div key={item.label} className={cn("rounded-md border border-border/60 bg-card/70 p-2", item.label === "Failed" && item.value > 0 && "border-destructive/30 bg-destructive/5")}>
                              <div className="text-sm font-semibold tabular-nums">{item.value}</div>
                              <div className="truncate text-[11px] text-muted-foreground">{item.label}</div>
                            </div>
                          ))}
                        </div>

                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-1.5">
                            {blockers.length ? blockers.map((blocker) => <Badge key={blocker} variant="secondary" className="text-[11px]">{blocker}</Badge>) : <Badge variant="outline" className="border-primary/30 bg-primary/10 text-[11px] text-primary">All launch checks passed</Badge>}
                          </div>
                          <div className="flex flex-wrap justify-start gap-2 xl:justify-end">
                            {campaign.status === "draft" && <Button size="sm" variant="outline" onClick={() => updateCampaignStatus(campaign, "approved")}><CheckCircle2 className="mr-1 h-3 w-3" />Approve</Button>}
                            <Button size="sm" variant="outline" onClick={() => openSequenceEditor(campaign)}>Edit</Button>
                            <Button size="sm" variant="outline" disabled={!hasSequence} onClick={() => setTestCampaign(campaign)}><Send className="mr-1 h-3 w-3" />Test</Button>
                            <Button size="sm" disabled={!hasSequence || campaign.status === "draft"} onClick={() => setEnrollCampaign(campaign)}><Users className="mr-1 h-3 w-3" />Enroll</Button>
                            <Button size="sm" variant="outline" onClick={() => setLogsCampaign(campaign)}><FileText className="h-3 w-3" /></Button>
                            {campaign.status === "active" ? <Button size="sm" variant="outline" onClick={() => updateCampaignStatus(campaign, "paused")}><Pause className="h-3 w-3" /></Button> : <Button size="sm" variant="outline" disabled={campaign.status === "draft"} onClick={() => updateCampaignStatus(campaign, "active")}><Play className="h-3 w-3" /></Button>}
                            <Button size="sm" variant="outline" onClick={() => archiveCampaign(campaign)}><Archive className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {visibleCampaigns.length === 0 && <div className="rounded-lg border border-dashed py-10 text-center text-muted-foreground"><Rocket className="mx-auto mb-3 h-10 w-10 opacity-60" />No campaigns in this view yet.</div>}
                {compact && visibleCampaigns.length > displayedCampaigns.length && (
                  <div className="flex items-center justify-between rounded-lg border border-dashed border-border/70 bg-background/50 p-4">
                    <div>
                      <div className="text-sm font-semibold">{visibleCampaigns.length - displayedCampaigns.length} more campaigns</div>
                      <div className="text-xs text-muted-foreground">Open the dedicated Campaigns page to manage the full list.</div>
                    </div>
                    <Button variant="outline" onClick={() => navigate("/campaigns")}>View All<ArrowRight className="ml-2 h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <CampaignEnrollmentDialog
        open={!!enrollCampaign}
        onOpenChange={(open) => !open && setEnrollCampaign(null)}
        campaignId={enrollCampaign?.id || ""}
        campaignName={enrollCampaign?.name || "Campaign"}
        availableLeads={selectedLeads.length > 0 ? selectedLeads : leads}
        onEnrolled={() => {
          setEnrollCampaign(null);
          void loadCampaignOps();
          onRefreshParent?.();
        }}
      />

      <CampaignTestSendDialog
        open={!!testCampaign}
        onOpenChange={(open) => !open && setTestCampaign(null)}
        campaignId={testCampaign?.id || ""}
        campaignName={testCampaign?.name || "Campaign"}
        smsEnabled={readiness.smsEnabled}
        onSent={loadCampaignOps}
      />

      <CampaignDeliveryLogsDialog
        open={!!logsCampaign}
        onOpenChange={(open) => !open && setLogsCampaign(null)}
        campaignId={logsCampaign?.id || ""}
        campaignName={logsCampaign?.name || "Campaign"}
      />

      <SequenceBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        sequence={editingSequence}
        onSaved={() => {
          setBuilderOpen(false);
          void loadCampaignOps();
        }}
      />
    </div>
  );
}
